import React from 'react'

import {
  Box,
  Button,
  Variants as ButtonVariants,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'

import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SettingsGroup from 'modules/security_settings/components/SettingsGroup'
import { useUpdatePasswordRotation } from 'modules/security_settings/pages/SecuritySettingsPage/PasswordRotationSection/useUpdatePasswordRotation'
import { TypedSecuritySettingsSecuritySettingsPageFragment } from 'modules/security_settings/pages/SecuritySettingsPage/useCompanyConfig'

interface PasswordRotationFormValues {
  passwordRotationEnabled: TypedSecuritySettingsSecuritySettingsPageFragment['passwordRotationEnabled']
  passwordRotationExpireInterval: TypedSecuritySettingsSecuritySettingsPageFragment['passwordRotationExpireInterval']
}

interface Props {
  passwordRotationEnabled: TypedSecuritySettingsSecuritySettingsPageFragment['passwordRotationEnabled']
  passwordRotationExpireInterval: TypedSecuritySettingsSecuritySettingsPageFragment['passwordRotationExpireInterval']
}

interface ExpireButtonData {
  days: number
  selected: boolean
  typeButton: ButtonVariants
}

const PASSWORD_ROTATION_EXPIRE_INTERVAL_VALUES = [30, 60, 90, 180, 365]
const PASSWORD_ROTATION_EXPIRE_INTERVAL_DEFAULT = 365

function PasswordRotationForm({ passwordRotationEnabled, passwordRotationExpireInterval }: Props) {
  const { updatePasswordRotation, loading: updating } = useUpdatePasswordRotation()

  passwordRotationExpireInterval =
    passwordRotationExpireInterval ?? PASSWORD_ROTATION_EXPIRE_INTERVAL_DEFAULT

  const form = useForm<PasswordRotationFormValues>({
    defaultValues: {
      passwordRotationEnabled,
      passwordRotationExpireInterval,
    },
    onSubmit: async (data) => {
      const result = await updatePasswordRotation(
        data.passwordRotationEnabled,
        data.passwordRotationExpireInterval ?? PASSWORD_ROTATION_EXPIRE_INTERVAL_DEFAULT
      )
      const handled = handleResult(result, (_formErrors) => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('security_settings.password_rotation.form.error_flash'),
          time: 60000, // 1 minute
        })
      })
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        const passwordRotationEnabled = handled.data.companyConfig?.passwordRotationEnabled ?? false
        const passwordRotationExpireInterval =
          handled.data.companyConfig?.passwordRotationExpireInterval ??
          PASSWORD_ROTATION_EXPIRE_INTERVAL_DEFAULT
        form.resetForm({ values: { passwordRotationEnabled, passwordRotationExpireInterval } })
      }
      return handled
    },
  })

  const expireButtonsData: ExpireButtonData[] = PASSWORD_ROTATION_EXPIRE_INTERVAL_VALUES.map(
    (days) => ({
      days,
      selected: days === form.values.passwordRotationExpireInterval,
      typeButton: days === form.values.passwordRotationExpireInterval ? 'positive' : 'secondary',
    })
  )

  function handlePasswordRotationChange(nextToggled: boolean) {
    form.setFieldValue('passwordRotationEnabled', nextToggled)
  }

  function handleExpireIntervalClick(value: number) {
    form.setFieldValue('passwordRotationExpireInterval', value)
  }

  // TODO: Refactor this function, that's is trowing a React warning:
  // Warning: React has detected a change in the order of Hooks called by Button. This will lead to bugs and errors if not fixed. For more information, read the Rules of Hooks: https://reactjs.org/link/rules-of-hooks
  // Because we are modifying the type of the button inside the map function
  function showButton({ days, selected, typeButton }: ExpireButtonData) {
    return (
      <Box width="fit-content" key={days}>
        <Button
          onClick={() => handleExpireIntervalClick(days)}
          type={typeButton}
          disabled={updating || !form.values.passwordRotationEnabled}
          iconName={selected && 'check'}
        >
          {i18n.t(
            'security_settings.password_rotation.form.password_rotation_expire_interval.option',
            { days: days }
          )}
        </Button>
      </Box>
    )
  }

  return (
    <form onSubmit={form.handleSubmit}>
      <Box width="full" gap="s32">
        <SettingsGroup>
          <Box width="full" flexDirection="row" justifyContent="spaceBetween">
            <Text weight="semibold" size="300" color="grey1000">
              {i18n.t('security_settings.password_rotation.form.password_rotation_enabled.label')}
            </Text>
            <TogglerCheckbox
              value={passwordRotationEnabled}
              onChange={handlePasswordRotationChange}
              disabled={updating}
            />
          </Box>
          <Box width="full">
            <Text weight="semibold" size="300" color="grey700">
              {i18n.t(
                'security_settings.password_rotation.form.password_rotation_expire_interval.label'
              )}
            </Text>
          </Box>
          <Box width="full" flexDirection="row" flexWrap="wrap" gap="s8">
            {expireButtonsData.map((button) => showButton(button))}
          </Box>
        </SettingsGroup>
        <Box width="full" flexDirection="rowReverse">
          <Button disabled={!form.dirty || updating} submit>
            {i18n.t('common.save_changes')}
          </Button>
        </Box>
      </Box>
    </form>
  )
}

export default React.memo(PasswordRotationForm)
